import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import ProductModelForm from "./ProductModelForm";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getUserRole } from "../../utils/auth";

const ProductModels = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [productModels, setProductModels] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole());
  }, []);

  const getProductModels = async () => {
    const { current, pageSize } = paginationInfo;
    try {
      setLoading(true);
      const response = await API.get(
        `/product-model?page=${current}&limit=${pageSize}`
      );
      const { data, pagination } = response?.data || {};
      setProductModels(data || []);
      setPaginationInfo((prev) => ({
        ...prev,
        total: pagination?.total || 0,
      }));
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductModels();
  }, [paginationInfo.current, paginationInfo.pageSize]);

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteProduct = async () => {
    try {
      await API.delete(`/product-model/${productToDelete?._id}`);
      message.success("Product deleted successfully!");
      getProductModels();
    } catch (err) {
      console.error("Error deleting product:", err);
      message.error("Failed to delete product.");
    } finally {
      setIsDeleteModalVisible(false);
    }
  };

  const handleAddProduct = async (newProduct) => {
    try {
      await API.post("/product-model", newProduct);
      message.success("Product added successfully!");
      getProductModels();
    } catch (err) {
      console.error(err);
      message.error("Failed to add product.");
    }
  };

  const handleEditProduct = async (updatedProduct) => {
    try {
      await API.put(`/product-model/${updatedProduct?._id}`, updatedProduct);
      message.success("Product updated successfully!");
      getProductModels();
      setOpenDrawer(false);
    } catch (err) {
      console.error(err.response?.data?.message);
      message.error(err.response?.data?.message || "Failed to update product.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Product Model Number",
      dataIndex: "productModelNumber",
      key: "productModelNumber",
    },
    {
      title: "Cost Price",
      dataIndex: "costPrice",
      key: "costPrice",
    },
    {
      title: "Selling Price",
      dataIndex: "sellingPrice",
      key: "sellingPrice",
    },
    userRole.editPermission == true && {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setIsEdit(true);
              setSelectedProduct(record);
              setOpenDrawer(true);
            }}
          />
          {/* Uncomment if you want delete button visible */}
          {/* <Button
            danger
            icon={<DeleteOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => handleDeleteClick(record)}
          /> */}
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedProduct(null);
            setOpenDrawer(true);
          }}
        >
          Add New Product
        </Button>
      </div>

      <CustomTable
        columns={columns}
        dataSource={productModels}
        onPaginationChange={(pagination) =>
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          })
        }
        loading={loading}
        style={{ padding: "0 20px" }}
        total={paginationInfo.total}
      />

      <ProductModelForm
        open={openDrawer}
        setOpen={setOpenDrawer}
        data={selectedProduct}
        isEdit={isEdit}
        onSubmit={isEdit ? handleEditProduct : handleAddProduct}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteProduct}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this product? This action cannot be undone."
      />
    </div>
  );
};

export default ProductModels;
